import React, { useState } from 'react';
import environment from 'environment';
import moment from 'moment';
import i18n from '../../i18n';
import { Tooltip } from "antd";

import SectionModel from '../../models/sectionModel';
import FilterOptionsModel from '../../models/filterOptionsModel';
import {
  CONTROL_TYPE, DELIVERY_STRATEGY, deliveryTypes,
  FILTER_TYPE, filterFieldTypes, getAccountOrderDeliveryStrategyList, getCurrencyIcons,
  getOrderIntegrationTypeFilterItems, getOrderStatusLabelText,
  INTEGRATION_TYPE,
  MODEL_CONTROL_TYPE,
  ORDER_INTEGRATION_TYPE_FILTER,
  ORDER_STATE,
  ORDER_TYPE, orderStatusLabelTexts,
} from '../../utils/enums';
import { DATE_FORMAT, DATE_TIME_FORMAT, getFormattedDate, getRelativeTime, isDefaultDate, minutesToTimeRelative, setEndTime, setStartTime } from '../../utils/convertTime';
import { getDeliveryDuration } from '../order';
import { getControlCenterFilter, groupBarOrderFilter, sortItemsDate } from '../filter';
import { createControlModel } from '../controlModelFactory';
import {
  getControlCenterNameById,
  getControlCenters, getDepotNameById,
  getDepots,
  getTerritoriesEntity,
} from '../restaurant.js';
import Actions from '../../app/components/ActiveOrder/Actions';
import { getCurrentLanguage } from '../userModel.js';
import IconButton from "../../app/components/IconButton/index.js";
import TargetTimeTable from "../../app/components/TargetTimeTable/index.js";
import EditCourierDelivery from "../../app/components/EditCourierDelivery/index.js";
import { createAction } from '../../utils/sagaHelper.js';
import { CONTROL_CENTER_REFRESH } from '../../store/reducers/restaurant/restaurant-actions.js';
import { getTotalPaymentForOrder } from '../../utils/order.js';
import { objectKeysToUpperLowerCase } from '../../utils/objects-util.js';
import { fixedNumber } from '../../utils/math-util.js';
import SummaryOrderFilter from '../../app/components/SummaryOrderFilter/SummaryOrderFilter.jsx';
import { AddOrderButton } from '../../app/components/TableAddButton/index.js';
import { GET_ADMIN_ORDER_REPORT } from '../../store/reducers/order/order-actions.js';
import SaveReportPopup from '../../app/components/SaveReportPopup/SaveReportPopup.jsx';
import ExportFileControl from '../../app/components/ExportFileControl/ExportFileControl.jsx';
import TimeBox from '../../app/components/TimeBox/TimeBox.jsx';

const ReportComponent = () => {
  const [isOpenFilterForm, setIsOpenFilterForm] = useState(false);
  return <ExportFileControl
    portalClassName={"exportControlPortal"}
    control={{ action: GET_ADMIN_ORDER_REPORT, actionFn: () => setIsOpenFilterForm(true) }}
    FilterForm={SaveReportPopup}
    filterFormProps={{
      isOpen: isOpenFilterForm,
      onClose: () => setIsOpenFilterForm(false),
    }}
  />
}

export function createOrdersSection() {
  const renderTextComponent = (text, trimTo) => {
    if (!text) return "";
    const shortText = text.length > (trimTo || 20) ? `${text.slice(0, (trimTo || 20))?.trim()}...` : text;
    return (
      <Tooltip title={text}>
        <span>{shortText}</span>
      </Tooltip>
    );
  }

  const lang = getCurrentLanguage();

  return new SectionModel({
    filterOptions: createOrdersSectionFilter(),
    columns: [
      {
        accessor: 'deliveryMethod',
        title: i18n.t('orders.tableColumns.TYPE'),
        width: 200,
        search: true,
        type: 'lookup',
        lookupOptions: () => deliveryTypes(),
        component: (_, item) => {
          const typeIcon = item.IsDineIn
            ? 'dinein'
            : item.Type === ORDER_TYPE.delivery
              ? 'carbonScooter'
              : 'carbonTakeaway';
          return <IconButton isIcon iconName={typeIcon} />;
        },
        canHideColumn: true,
      },
      {
        accessor: 'date',
        title: i18n.t('orders.tableColumns.DATE'),
        search: true,
        enableSortColumn: true,
        width: 205,
        component: (_, item) => isDefaultDate(item.Date) ? '' : getFormattedDate(item.Date, DATE_TIME_FORMAT),
        canHideColumn: true,
        alwaysOnFilter: true,
        hideFilterFromPreview: true,
        shortFilter: true,
        type: 'date',
      },
      {
        accessor: 'depotId',
        title: i18n.t('orders.tableColumns.DEPOT'),
        search: true,
        width: 205,
        canHideColumn: true,
        enableSortColumn: true,
        type: 'lookup',
        maxLookupQuantity: 10,
        hideSelectAll: true,
        lookupOptions: () => getDepots(false).map(d => ({ value: d.name, key: d.id })),
        component: (_, item) => {
          return renderTextComponent(getDepotNameById(item?.DepotId));
        },
      },
      {
        accessor: 'city',
        title: i18n.t('orders.tableColumns.ADDRESS_CITY'),
        width: 205,
        canHideColumn: true,
        search: true,
        type: 'string',
        component: (_, item) => renderTextComponent(item?.Address?.City),
      },
      {
        accessor: 'street',
        title: i18n.t('orders.tableColumns.ADDRESS_STREET'),
        width: 205,
        canHideColumn: true,
        search: true,
        type: 'string',
        component: (_, item) => renderTextComponent(item?.Address?.Street),
      },
      {
        accessor: 'addressNotes',
        title: i18n.t('orders.tableColumns.ADDRESS_NOTES'),
        width: lang === 'ru' ? 255 : 205,
        canHideColumn: true,
        search: true,
        type: 'string',
        component: (_, item) => renderTextComponent(item?.Address?.Notes),
      },
      {
        accessor: 'totalDeliveryTimeInMin',
        title: i18n.t('orders.tableColumns.TOTAL_DELIVERY_TIME'),
        width: lang === 'ru' ? 255 : 205,
        canHideColumn: true,
        search: false,
        type: 'number',
        component: (_, item) => getRelativeTime(minutesToTimeRelative(item.TotalDeliveryTimeInMin)),
      },
      {
        accessor: 'status',
        title: i18n.t('orders.tableColumns.STATUS'),
        width: 200,
        search: true,
        component: (_, item) => {
          const showRelatedTime = ![ORDER_STATE.canceled, ORDER_STATE.delivered].includes(item.State) ? <span className="relativeTime"><TimeBox date={item?.TimesInfo?.OrderCreationTime} /></span> : null;
          return (
            <div className="statusContainer">
              <div className={`statusBlock statusBlock__${item?.State}`}>
                {getOrderStatusLabelText(item?.State, item.Type)}
              </div>
              {showRelatedTime && !!item?.TimesInfo?.OrderCreationTime && (
                <div className="relativeTimeContainer">
                  {i18n.t("admin-orders.CREATION_DATE_TIME_AGO_LABEL")}
                  {showRelatedTime}
                </div>
              )}
            </div>
          )
        },
        canHideColumn: true,
        type: 'lookup',
        lookupOptions: orderStatusLabelTexts,
      },
      {
        accessor: 'controlCenterId',
        title: i18n.t('orders.tableColumns.CONTROL_CENTER'),
        width: lang === 'ru' ? 245 : 205,
        search: true,
        canHideColumn: true,
        lookupOnApply: selected => {
          const cc = getControlCenters(false);
          const ids = selected.map(center => {
            const find = cc.find(c => c.name === center);
            return find?.controlCenterId || null;
          });
          return createAction(CONTROL_CENTER_REFRESH, {
            selectedControlCenterMulti: ids,
          });
        },
        maxLookupQuantity: 10,
        hideSelectAll: true,
        lookupOptions: () => [...getControlCenters(false).map(cc => ({ value: cc.name, key: cc.controlCenterId }))],
        type: 'lookup',
        component: (_, item) => renderTextComponent(getControlCenterNameById(item.ControlCenterId)),
      },
      {
        accessor: 'phone',
        title: i18n.t('orders.tableColumns.PHONE'),
        width: lang === 'ru' ? 160 : 150,
        search: true,
        canHideColumn: true,
        type: 'string',
        filterPlaceholder: i18n.t('orders.CENTER_PHONE_FILTER_LABEL'),
        component: (_, item) => renderTextComponent(item?.ConsumerPhone, 15),
      },
      {
        accessor: 'fullName',
        title: i18n.t('orders.tableColumns.FULL_NAME'),
        search: true,
        width: 205,
        canHideColumn: true,
        type: 'string',
        component: (_, item) => {
          return renderTextComponent(`${(item?.FirstName || '').trim()} ${(item?.LastName || '').trim()}`.trim())
        },
      },
      {
        accessor: 'cashAmount',
        title: i18n.t('orders.tableColumns.CASH'),
        width: 200,
        search: true,
        canHideColumn: true,
        component: (amount, item) => (
          <div>
            {getTotalPaymentForOrder(item.Payments).cash} {getCurrencyIcons(item.Currency)}
          </div>
        ),
        type: 'number',
      },
      {
        accessor: 'cardAmount',
        title: i18n.t('orders.tableColumns.CARD'),
        width: 200,
        search: true,
        canHideColumn: true,
        component: (amount, item) => (
          <div>
            {getTotalPaymentForOrder(item.Payments).card} {getCurrencyIcons(item.Currency)}
          </div>
        ),
        type: 'number',
      },
      {
        accessor: 'courierCompensation',
        title: i18n.t('orders.ORDER_COMPENSATION_TOOLTIP'),
        width: 200,
        search: true,
        canHideColumn: true,
        component: (amount, item) => (
          <div>
            {item.CourierCompensation} {getCurrencyIcons(item.Currency)}
          </div>
        ),
        type: 'number',
      },
      {
        accessor: 'courierDeliveryFee',
        title: i18n.t('admin-orders.ORDER_SUMMARY_DELIVERY_FEE_LABEL'),
        width: 200,
        search: true,
        canHideColumn: true,
        component: (amount, item) => (
          <div>
            {fixedNumber(item.CourierDeliveryFee || 0, 2)} {getCurrencyIcons(item.Currency)}
          </div>
        ),
        type: 'number',
      },
      {
        accessor: 'courierFeeBidIncrease',
        title: i18n.t('orders.ORDER_COURIER_BID'),
        width: 230,
        search: true,
        canHideColumn: true,
        component: (amount, item) => (
          <div>
            {fixedNumber(item.CourierFeeBidIncrease * 100, 2) || 0}%
          </div>
        ),
        type: 'number',
      },
      {
        accessor: 'driverTip',
        title: i18n.t('edit-customer-order.COURIER_TIP'),
        width: 200,
        search: true,
        canHideColumn: true,
        component: (amount, item) => (
          <div>
            {item.DriverTip} {getCurrencyIcons(item.Currency)}
          </div>
        ),
        type: 'number',
      },
      {
        accessor: 'deliveryStrategy',
        title: i18n.t('orders.tableColumns.DELIVERY_STRATEGY'),
        width: lang === 'ru' ? 250 : 215,
        canHideColumn: true,
        search: true,
        type: 'lookup',
        lookupOptions: () => [
          { key: DELIVERY_STRATEGY.asap, value: i18n.t('enums.accountOrderDeliveryStrategy.ASAP') },
          { key: DELIVERY_STRATEGY.concreteTime, value: i18n.t('enums.accountOrderDeliveryStrategy.CONCRETETIME') },
        ],
        component: (value, item) => getAccountOrderDeliveryStrategyList(item.DeliveryStrategy),
      },
      {
        accessor: 'courier',
        title: i18n.t('orders.tableColumns.COURIER'),
        width: 205,
        canHideColumn: true,
        search: true,
        type: 'lookup',
        component: (_, item) => <EditCourierDelivery order={{ id: item.Id, deliveryBy: item.DeliveryBy, deliveryProvider: item.DeliveryProvider, deliveryProviderId: item.DeliveryProviderId }} />,
      },
      {
        accessor: 'serviceNotes',
        title: i18n.t('orders.tableColumns.SERVICE_NOTES'),
        width: lang === 'ru' ? 310 : 205,
        search: true,
        canHideColumn: true,
        type: 'string',
        component: (_, item) => renderTextComponent(item?.ServiceNotes),
      },
      {
        accessor: 'realOrderId',
        width: lang === 'ru' ? 205 : lang === 'he' ? 180 : 150,
        search: true,
        title: i18n.t('orders.tableColumns.ORDER_ID'),
        canHideColumn: true,
        type: 'string',
        component: (_, item) => renderTextComponent(item.OrderId),
      },
      {
        accessor: 'targetTime',
        title: i18n.t('orders.tableColumns.TARGET_TIME'),
        minFilterAccessorValue: 'value',
        limitMinMax: true,
        enableSortColumn: true,
        search: true,
        component: (_, item) => <TargetTimeTable order={item} />,
        width: 260,
        canHideColumn: true,
        type: 'date',
      },
      {
        accessor: 'assistanceTime',
        title: i18n.t('orders.EXTERNAL_PROVIDER_ASSIGNMENT_TIME'),
        enableSortColumn: true,
        minFilterAccessorValue: 'value',
        limitMinMax: true,
        search: true,
        component: (_, item) =>
          !!item?.TimesInfo?.ExternalProviderAssignmentTime &&
          !isDefaultDate(item.TimesInfo.ExternalProviderAssignmentTime) &&
          getFormattedDate(item.TimesInfo.ExternalProviderAssignmentTime, DATE_TIME_FORMAT),
        width: ['sl', 'ru'].includes(i18n.language) ? 310 : 280,
        canHideColumn: true,
        type: 'date',
      },
      {
        accessor: 'readinessTime',
        title: i18n.t('orders.tableColumns.READINESS_TIME'),
        minFilterAccessorValue: 'value',
        enableSortColumn: true,
        limitMinMax: true,
        search: true,
        component: (_, item) => isDefaultDate(item.ReadinessTime) ? '' : getFormattedDate(item.ReadinessTime, DATE_TIME_FORMAT),
        width: lang === 'ru' ? 245 : 205,
        canHideColumn: true,
        type: 'date',
      },
      {
        accessor: 'deliveredAt',
        title: i18n.t('orders.tableColumns.DELIVERY_TIME'),
        search: true,
        minFilterAccessorValue: 'value',
        enableSortColumn: true,
        limitMinMax: true,
        component: (_, item) => isDefaultDate(item.DeliveryTime) ? '' : getFormattedDate(item.DeliveryTime, DATE_TIME_FORMAT),
        width: lang === 'ru' ? 245 : 205,
        canHideColumn: true,
        type: 'date',
      },
      {
        accessor: 'actions',
        title: i18n.t('orders.tableColumns.ACTIONS'),
        component: (_, item) => <Actions item={objectKeysToUpperLowerCase(item)} />,
        fixed: 'right',
        // phones show a single centred "⋯" trigger — a 150px fixed overlay would hide row data
        width: (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 900px)').matches) ? 64 : 150,
      },
    ],
    sortByFunc: function (orders) {
      return sortItemsDate(getControlCenterFilter(orders, 'controlCenterId'), 'date', true);
    },
    hideHeaderItemControls: true,
    hideCellForSort: true,
    controls: [createControlModel({ type: MODEL_CONTROL_TYPE.filter })],
    typeModel: '/orders',
    isForceUpdate: true,
    customTableConfig: {
      factoryMap: filterFieldTypes,
      summaryComponent: SummaryOrderFilter,
      reportComponent: ReportComponent,
      addComponent: AddOrderButton,
      mainDateKey: 'date',
    },
  });
}

export function createOrdersSectionOld() {
  return new SectionModel({
    filterOptions: createOrdersSectionFilter(),
    tag: "activeOrder",
    sortByFunc: function (orders) {
      return sortItemsDate(getControlCenterFilter(orders, "controlCenterId"), "date", true);
    },
    controls: [
      createControlModel({ type: MODEL_CONTROL_TYPE.filter }),
    ],
    typeModel: "/orders",
    isForceUpdate: true,
  });
}

function createOrdersSectionFilter() {
  let filterOptionsModel = new FilterOptionsModel();
  const orderIntegrationTypeFilterItems = getOrderIntegrationTypeFilterItems();
  const territoriesItems = getTerritoriesEntity();

  //search
  filterOptionsModel.addOptions("search", CONTROL_TYPE.text, null, function (order) {
    return groupBarOrderFilter(this.selected, order);
  });

  //date
  let filterDate = filterOptionsModel.addOptions(
    "date",
    CONTROL_TYPE.date,
    i18n.t("dashboard.orders.ORDER_DATE_FILTER_LABEL"),
    function (order) {
      const orderDate = moment.utc(order.date);
      const selectedDate = moment.utc(setStartTime(this.selected));
      return selectedDate <= orderDate;
    },
    null,
    null,
    true,
    false,
    false,
    false,
    {
      format: DATE_FORMAT,
    }
  );
  filterDate.filterType = FILTER_TYPE.and;
  let filterDateEnd = filterOptionsModel.addOptions(
    "dateEnd",
    CONTROL_TYPE.date,
    i18n.t("dashboard.orders.ORDER_DATE_FILTER_LABEL"),
    function (order) {
      const orderDate = moment.utc(order.date);
      const selectedDate = moment.utc(setEndTime(this.selected));
      return orderDate <= selectedDate;
    },
    null,
    null,
    true,
    false,
    false,
    false,
    {
      format: DATE_FORMAT,
    }
  );
  filterDateEnd.filterType = FILTER_TYPE.and;

  //integrationTypes
  let integrationTypes = filterOptionsModel.addOptions("integrationTypes",
    CONTROL_TYPE.select,
    i18n.t("dashboard.orders.ORDER_INTEGRATION_TYPE_FILTER_LABEL"),
    function (order) {
      const type = orderIntegrationTypeFilterItems.find(f => f.key === this.selected);
      if (!type) return true;
      switch (type.key) {
        case ORDER_INTEGRATION_TYPE_FILTER.application:
          return order.integrationType === INTEGRATION_TYPE.ordering;
        case ORDER_INTEGRATION_TYPE_FILTER.external:
          return order.integrationType !== INTEGRATION_TYPE.ordering;
        default:
          return true;
      }
    }
  );
  integrationTypes.filterType = FILTER_TYPE.and;
  orderIntegrationTypeFilterItems.forEach(item => {
    integrationTypes.addItem(item.value, item.key);
  });
  integrationTypes.selected = orderIntegrationTypeFilterItems[2].key;


  //territories
  if (territoriesItems && territoriesItems.length) {
    const territoriesFilter = filterOptionsModel.addOptions(
      "territories",
      CONTROL_TYPE.select,
      i18n.t("dashboard.orders.ORDER_DELIVERY_TERRITORY_LABEL"),
      null,
      null,
      null,
      false,
      null,
      null,
      false
    );
    territoriesFilter.addItem("-", null);
    territoriesItems.forEach(t => {
      territoriesFilter.addItem(t.name, t.id);
    })
  }

  //deliveryGroup
  filterOptionsModel.addOptions(
    "deliveryGroup",
    CONTROL_TYPE.groupCheckbox,
    "dashboard.orders.ORDER_DELIVERY_TYPE_GROUP_LABEL",
    null,
    null,
    [
      {
        title: "dashboard.orders.ORDER_DELIVERY_TYPE_GROUP_LABEL",
        isCheckAllGroup: true,
        filterType: FILTER_TYPE.and,
        items: [
          {
            name: "takeaway",
            label: "orders.TAKEAWAY_FILTER_LABEL",
            selected: true,
            filterType: FILTER_TYPE.or,
            filterFunction: order => order.type === ORDER_TYPE.takeAway
          },
          {
            name: "delivery",
            label: "orders.DELIVERY_FILTER_LABEL",
            selected: true,
            filterType: FILTER_TYPE.or,
            filterFunction: order => order.type === ORDER_TYPE.delivery
          }
        ]
      },
      {
        title: "dashboard.orders.ORDER_ORDER_STATE_GROUP_LABEL",
        isCheckAllGroup: true,
        filterType: FILTER_TYPE.and,
        items: [
          {
            name: "pending",
            label: "orders.PENDING_FILTER_LABEL",
            selected: true,
            filterType: FILTER_TYPE.or,
            filterFunction: order => order.state === ORDER_STATE.new
          },
          {
            name: "inkitchen",
            label: "orders.INKITCHEN_FILTER_LABEL",
            selected: true,
            filterType: FILTER_TYPE.or,
            filterFunction: order => order.state === ORDER_STATE.kitchen
          },
          {
            name: "ready",
            label: "orders.READY_FILTER_LABEL",
            selected: true,
            filterType: FILTER_TYPE.or,
            filterFunction: order => order.state === ORDER_STATE.ready
          },
          {
            name: "onroute",
            label: "orders.ONROUTE_FILTER_LABEL",
            selected: true,
            filterType: FILTER_TYPE.or,
            filterFunction: order => order.state === ORDER_STATE.onroute
          },
          {
            name: "delivered",
            label: "orders.DELIVERED_FILTER_LABEL",
            selected: true,
            filterType: FILTER_TYPE.or,
            filterFunction: order => order.state === ORDER_STATE.delivered
          },
          {
            name: "deleted",
            label: "orders.DELETED_FILTER_LABEL",
            selected: true,
            filterType: FILTER_TYPE.or,
            filterFunction: order => order.state === ORDER_STATE.canceled
          }
        ]
      }
    ],
    false,
    null,
    null,
    true
  );

  //lateorders
  let selectOption = filterOptionsModel.addOptions(
    "lateorders",
    CONTROL_TYPE.select,
    i18n.t("dashboard.orders.ORDER_LATE_FOR_LABEL"),
    function (order) {
      if (!this.selected) return true;
      if (order.state !== ORDER_STATE.delivered)
        return false;
      let late = getDeliveryDuration(order);
      if (!late)
        return false;
      late = late / (1000 * 60);
      return late > this.selected;
    }
  );
  selectOption.filterType = FILTER_TYPE.and;
  selectOption.addItem("-", 0);
  selectOption.addItem(i18n.t("orders.LATE_FOR_HALF_LABEL"), 30);
  selectOption.addItem(i18n.t("orders.LATE_FOR_HOUR_LABEL"), 60);
  selectOption.addItem(i18n.t("orders.LATE_FOR_HOUR_AND_HALF_LABEL"), 90);
  selectOption.selected = 0;

  //historyTime
  let slider = filterOptionsModel.addOptions(
    "historyTime",
    CONTROL_TYPE.slider,
    i18n.t("dashboard.orders.ORDER_HISTORY_TIME_LABEL"),
    null,
    false,
    environment.orders.defaultHistoryTime,
    true
  );
  slider.filterType = FILTER_TYPE.and;
  slider.setMinMax(
    environment.orders.minHistoryTime,
    environment.orders.maxHistoryTime
  );

  return filterOptionsModel;
}
